'use client';

import { useRouter } from 'next/navigation';
import React, { createContext, useContext, useState, ReactNode, useEffect, Dispatch, SetStateAction, useRef, useCallback } from 'react';

// Actions
import { getProjectInfo } from '@/actions/client';

// Types
import { Project, ProjectMember, ProjectActivity, TimelineLoadingState, ActivityType, ProjectInfo } from '@shared/types';

// Constants
import { PROJECT_PAGE_MESSAGE, ROUTES, STATUS_CODE, STATUS_MESSAGES, UserRole } from '@shared/constants';

// UIs
import FullScreenLoader from '@/components/FullScreenLoader';
import { LoadingIndicator } from '@shared/ui';

// Types
import { MemberAddPayloadSocket, MemberProjectActivitySocket } from '@/types';

// Utils
import { deleteMembers, getUserRole, hasCodeProperty, hasMembersProperty, sortProjectMembers, updateMemberList } from '@/utils';
import { showErrorToast } from '@/utils/errorHandlers';

// Hooks
import { useGetProjectActivities } from '@/hooks/useGetProjectActivities';
import { useGetProjectMembers } from '@/hooks/useGetProjectMembers';
import { useWebSocketMessage } from '@/hooks/useWebSocket';
import { useFirebase } from './firebase';
export type ProjectContextType = {
  project: Project | null;
  members: ProjectMember[];
  activities: ProjectActivity[];
  isLoading: boolean;
  isProcessing: boolean;
  isInitialized: boolean;
  refetch: () => Promise<void>;
  refetchMembers: () => Promise<void>;
  refetchActivities: () => Promise<void>;
  setProject: Dispatch<SetStateAction<Project | null>>;
  setMembers: Dispatch<SetStateAction<ProjectMember[]>>;
  setActivities: Dispatch<SetStateAction<ProjectActivity[]>>;
  setIsProcessing: Dispatch<SetStateAction<boolean>>;
  hasMoreEvents: boolean;
  loadingEventsState: TimelineLoadingState;
  eventsError: string | null;
  loadMoreEvents: () => Promise<void>;
  retryEvents: () => Promise<void>;
  membersLoading: boolean;
  membersError: string | null;
  membersInitialLoaded: boolean;
};
export const ProjectContext = createContext<ProjectContextType | undefined>(undefined);
interface ProjectProviderProps {
  children: ReactNode;
  projectId: string;
}
export const ProjectProvider = ({
  children,
  projectId
}: ProjectProviderProps) => {
  const [project, setProject] = useState<Project | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const router = useRouter();
  const activitiesRef = useRef<ProjectActivity[]>([]);
  const {
    getToken,
    user
  } = useFirebase();
  const setActivitiesRef = useCallback((activities: ProjectActivity[] | ((prev: ProjectActivity[]) => ProjectActivity[])) => {
    activitiesRef.current = typeof activities === 'function' ? activities(activitiesRef.current) : activities;
  }, []);
  const {
    activities: rootActivities,
    hasMore: hasMoreEvents,
    loadingState: loadingEventsState,
    error: eventsError,
    loadMore: loadMoreEvents,
    retry: retryEvents,
    refetchActivities
  } = useGetProjectActivities({
    project,
    isInitialized,
    activities: activitiesRef.current,
    setActivities: setActivitiesRef
  });
  const {
    members: rootMembers,
    isLoading: membersLoading,
    error: membersError,
    isInitialLoaded: membersInitialLoaded,
    refetch: refetchMembers
  } = useGetProjectMembers({
    project,
    isInitialized,
    members,
    setMembers
  });
  const fetchProject = async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      const {
        data: projectData,
        status: projectStatus,
        error: projectError
      } = await getProjectInfo(projectId, {
        token
      });
      const isAccessDenied = projectError !== null && (
      // Check by message to make sure user is not banned
      STATUS_MESSAGES[STATUS_CODE.FORBIDDEN]?.includes(projectError) || projectStatus === STATUS_CODE.NOT_FOUND);
      if (isAccessDenied) {
        router.replace(ROUTES.NOT_FOUND);
        return;
      }
      if (projectError || !projectData) {
        showErrorToast(PROJECT_PAGE_MESSAGE.FETCH_PROJECT_INFO_FAILED);
        return;
      }
      setProject({
        projectInfo: projectData
      });
    } catch {
      showErrorToast(PROJECT_PAGE_MESSAGE.FETCH_PROJECT_INFO_FAILED);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };
  const refetch = async () => {
    await fetchProject();
  };
  useEffect(() => {
    if (!isInitialized) {
      fetchProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isInitialized]);

  // Reset state when projectId changes
  useEffect(() => {
    setProject(null);
    setActivitiesRef([]);
    setIsInitialized(false);
  }, [projectId, setActivitiesRef]);
  useWebSocketMessage<MemberProjectActivitySocket | MemberAddPayloadSocket | ProjectInfo>(async message => {
    const userRole = getUserRole();
    if (!message?.channel || !message?.payload) return;
    if (message.channel === ActivityType.ACTIVITY_UPDATE && 'activities' in message.payload) {
      // Type assertion to properly narrow the type
      const dataPayload = message.payload as MemberProjectActivitySocket;
      const [newActivity] = dataPayload.activities;
      if (newActivity && activitiesRef.current.some(activity => activity.id === newActivity.id)) {
        await refetchActivities();
      }
    }

    // Just update the current project
    if ('id' in message.payload && message.payload.id !== project?.projectInfo.id || !('code' in message.payload) || 'code' in message.payload && message.payload.code !== project?.projectInfo.code) {
      return;
    }
    switch (message.channel) {
      case ActivityType.PROJECT_ARCHIVE:
      case ActivityType.PROJECT_UNARCHIVE:
        if (hasCodeProperty(message.payload)) {
          const projectPayload = message.payload as ProjectInfo;
          setProject(prev => ({
            projectInfo: {
              ...prev?.projectInfo,
              ...projectPayload,
              code: projectPayload.code || prev?.projectInfo.code,
              archived: projectPayload.archived
            }
          }));
          await refetchActivities();
        }
        break;
      case ActivityType.PROJECT_UPDATE:
      case ActivityType.PROJECT_KICK_OFF:
      case ActivityType.PROJECT_RELEASE:
      case ActivityType.PROJECT_GO_LIVE:
        if (hasCodeProperty(message.payload)) {
          const projectPayload = message.payload as ProjectInfo;
          setProject(prev => ({
            projectInfo: {
              ...prev?.projectInfo,
              ...projectPayload
            }
          }));
          await refetchActivities();
        }
        break;
      case ActivityType.PROJECT_MEMBER_JOIN:
      case ActivityType.PROJECT_MEMBER_LEAVE:
      case ActivityType.PROJECT_MEMBER_ROLE_CHANGE:
      case ActivityType.PROJECT_MEMBER_RESUME:
        if (hasMembersProperty(message.payload)) {
          const projectPayload = message.payload as MemberAddPayloadSocket;
          const formattedMembers = projectPayload.members;
          if (formattedMembers.length > 0) {
            setMembers(prev => {
              const newMemberList = updateMemberList(formattedMembers, prev);
              const sortedMembers = sortProjectMembers(newMemberList);
              return sortedMembers;
            });
          }
          if (userRole === UserRole.USER && message.channel === ActivityType.PROJECT_MEMBER_LEAVE && projectPayload.members.some(item => item.user === user?.email)) {
            router.replace(ROUTES.NOT_FOUND);
            return;
          }
          await refetchActivities();
        }
        break;
      case ActivityType.PROJECT_MEMBER_REMOVE:
        if (hasMembersProperty(message.payload)) {
          const projectPayload = message.payload as MemberAddPayloadSocket;
          const emailList = projectPayload.members.map(member => member.user || '');
          if (emailList && emailList.length > 0) {
            setMembers(prev => {
              const newMemberList = deleteMembers(emailList, prev);
              const formattedMembers = sortProjectMembers(newMemberList);
              return formattedMembers;
            });
          }
          if (userRole === UserRole.USER && emailList.some(item => item === user?.email)) {
            router.replace(ROUTES.NOT_FOUND);
            return;
          }
          await refetchActivities();
        }
        break;
      case ActivityType.PROJECT_PAUSE:
      case ActivityType.PROJECT_RESUME:
      case ActivityType.PROJECT_COMPLETE:
        if (typeof message.payload === 'object') {
          const projectPayload = message.payload as MemberAddPayloadSocket;
          if (hasMembersProperty(message.payload)) {
            const formattedMembers = projectPayload.members;
            if (formattedMembers.length > 0) {
              setMembers(prev => {
                const newMemberList = updateMemberList(formattedMembers, prev);
                const sortedMembers = sortProjectMembers(newMemberList);
                return sortedMembers;
              });
            }
          }
          if (hasCodeProperty(message.payload)) {
            setProject(prev => ({
              projectInfo: {
                ...prev?.projectInfo,
                ...projectPayload
              }
            }));
          }
          await refetchActivities();
        }
        break;
      default:
        return;
    }
  });
  const value: ProjectContextType = {
    project,
    isLoading,
    isProcessing,
    isInitialized,
    refetch,
    refetchMembers,
    refetchActivities,
    setProject,
    setIsProcessing,
    members: rootMembers,
    activities: rootActivities,
    setMembers,
    setActivities: setActivitiesRef,
    hasMoreEvents,
    loadingEventsState,
    eventsError,
    loadMoreEvents,
    retryEvents,
    membersLoading,
    membersError,
    membersInitialLoaded
  };
  return <ProjectContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="ProjectProvider" data-sentry-source-file="project.tsx">
      {/* Render loading state while fetching project data */}
      {isLoading || !isInitialized ? <FullScreenLoader /> : !project ? null :
    // Project loaded successfully, render children with processing overlay
    <>
          {isProcessing && <div className="fixed inset-0 bg-background/2 backdrop-blur-sm z-50 flex-center transition-opacity duration-300">
              <LoadingIndicator size="lg" color="primary" center className="min-h-48" />
            </div>}

          {children}
        </>}
    </ProjectContext.Provider>;
};
export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};